<script setup lang="ts">
import { ChevronLeft, Pin, X } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button } from '@/components/ui/button';
import SidebarNavItem from '@components/atoms/SidebarNavItem.vue';
import SidebarBrand from '@components/molecules/SidebarBrand.vue';
import SidebarNav from '@components/molecules/SidebarNav.vue';
import SidebarUserMenu from '@components/molecules/SidebarUserMenu.vue';
import { useSidebar } from '@composables/useSidebar';

const { isExpanded, isPinned, isMobile, close, togglePin } = useSidebar();
const { t } = useI18n();

const showBackdrop = computed(() => {
  if (isMobile.value) {
    return isExpanded.value;
  }

  return isExpanded.value && !isPinned.value;
});
</script>

<template>
  <!-- Desktop sidebar (always visible, width transitions) -->
  <aside
    v-if="!isMobile"
    class="bg-background border-border fixed inset-y-0 left-0 z-30 flex flex-col overflow-hidden border-r transition-[width] duration-200 ease-in-out"
    :style="{ width: isExpanded ? '16rem' : '4.5rem' }"
  >
    <div class="flex items-center" :class="isExpanded ? 'justify-between pr-2' : 'justify-center'">
      <SidebarBrand />

      <div v-if="isExpanded" class="flex flex-shrink-0 items-center gap-1">
        <!-- Pin toggle -->
        <Button
          variant="ghost"
          size="icon-sm"
          :class="isPinned ? 'text-primary bg-primary/10' : ''"
          :title="isPinned ? t('sidebar.unpin') : t('sidebar.pin')"
          @click="togglePin"
        >
          <Pin
            :size="16"
            class="origin-center transition-transform duration-200"
            :class="isPinned ? '' : 'rotate-45'"
          />
        </Button>

        <!-- Collapse button -->
        <Button variant="ghost" size="icon-sm" :title="t('sidebar.close')" @click="close">
          <ChevronLeft :size="16" />
        </Button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto py-2">
      <SidebarNav />
    </div>

    <div class="border-border flex flex-col gap-1 border-t p-2">
      <div class="px-1">
        <SidebarNavItem to="/settings" :label="t('sidebar.settings')">
          <template #icon>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </template>
        </SidebarNavItem>
      </div>

      <div class="px-1">
        <SidebarUserMenu />
      </div>
    </div>
  </aside>

  <!-- Backdrop -->
  <Teleport to="body">
    <Transition name="sidebar-overlay">
      <div
        v-if="showBackdrop"
        class="fixed inset-0 bg-black/50"
        :class="isMobile ? 'z-40' : 'z-20'"
        @click="close"
      />
    </Transition>
  </Teleport>

  <!-- Mobile sidebar (overlay drawer) -->
  <Teleport to="body">
    <Transition name="sidebar-slide">
      <aside
        v-if="isMobile && isExpanded"
        class="bg-background fixed inset-y-0 left-0 z-50 flex w-64 flex-col shadow-xl"
      >
        <div class="flex items-center justify-between pr-2">
          <SidebarBrand />

          <Button variant="ghost" size="icon-sm" @click="close">
            <X :size="16" />
          </Button>
        </div>

        <div class="flex-1 overflow-y-auto py-2">
          <SidebarNav />
        </div>

        <div class="border-border flex flex-col gap-1 border-t p-2">
          <div class="px-1">
            <SidebarNavItem to="/settings" :label="t('sidebar.settings')">
              <template #icon>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </template>
            </SidebarNavItem>
          </div>

          <div class="px-1">
            <SidebarUserMenu />
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>
